import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';
import { GUIDES, AUTHOR, AUTHOR_URL, HELP_ADVICE_INDEX_URL } from '../data/guides';

/** Help & Advice — landing page that lists all editorial guides. */
const HelpAdvicePage = () => {
  const siteOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://drtoolsuk.com';
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Dr Tools UK — Help & Advice',
    itemListElement: GUIDES.map((g, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${siteOrigin}/help-advice/${g.slug}`,
      name: g.title,
    })),
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteOrigin}/` },
      { '@type': 'ListItem', position: 2, name: 'Help & Advice', item: `${siteOrigin}${HELP_ADVICE_INDEX_URL}` },
    ],
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-8" data-testid="help-advice-page">
      <SEO
        title="Help & Advice — Expert Guides on Flooring, Decking & Cladding"
        description="Buying guides and how-to articles from the Dr Tools UK team, based on 18+ years shipping composite decking, SPC/LVT flooring and exterior cladding to UK trade and self-build."
        canonical={`${siteOrigin}${HELP_ADVICE_INDEX_URL}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <nav className="text-xs text-neutral-500 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        <span className="mx-1">/</span>
        <span className="text-[#12263A] font-semibold">Help &amp; Advice</span>
      </nav>

      <div className="mb-8">
        <span className="inline-flex items-center gap-1.5 bg-orange-50 text-[#F46A25] text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
          <BookOpen className="w-3 h-3" /> Editorial
        </span>
        <h1 className="text-3xl md:text-5xl font-black text-[#12263A] mt-2">Help &amp; Advice</h1>
        <p className="text-neutral-600 text-lg mt-3 max-w-3xl">
          Long-form buying guides written by our founder and reviewed by the fitters we work with. No sponsored copy — just what has worked (and what has not) on UK trade projects.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {GUIDES.map(g => (
          <article
            key={g.slug}
            data-testid={`help-advice-card-${g.slug}`}
            className="bg-white border border-neutral-200 hover:border-[#F46A25] hover:shadow-lg transition-all rounded-sm overflow-hidden flex flex-col"
          >
            <Link to={`/help-advice/${g.slug}`} className="block aspect-[16/9] overflow-hidden bg-neutral-100">
              <img
                src={g.hero_image}
                alt={g.title}
                width="1200"
                height="675"
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </Link>
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-[10px] text-[#F46A25] font-bold uppercase tracking-wider mb-1">{g.category}</div>
              <Link to={`/help-advice/${g.slug}`} className="text-lg font-black text-[#12263A] hover:text-[#F46A25] leading-tight mb-2 line-clamp-2">{g.title}</Link>
              <p className="text-sm text-neutral-600 line-clamp-3 flex-1">{g.excerpt}</p>
              <div className="flex items-center justify-between text-[11px] text-neutral-500 mt-3 pt-3 border-t border-neutral-100">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {g.reading_time} min read</span>
                <Link to={`/help-advice/${g.slug}`} className="flex items-center gap-1 text-[#F46A25] font-bold hover:underline">
                  Read guide <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>

      <section className="mt-12 bg-neutral-50 border border-neutral-200 rounded-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <img src={AUTHOR.image} alt={AUTHOR.name} width="80" height="80" className="w-20 h-20 rounded-full bg-white border border-neutral-200 object-contain p-2" />
          <div className="flex-1">
            <div className="text-[10px] text-[#F46A25] font-bold uppercase tracking-wider">Written by</div>
            <h2 className="text-xl font-black text-[#12263A]">{AUTHOR.name}</h2>
            <div className="text-sm text-neutral-500 font-semibold">{AUTHOR.role}</div>
            <p className="text-sm text-neutral-700 mt-2">{AUTHOR.short_bio}</p>
            <Link to={AUTHOR_URL} className="inline-block mt-2 text-sm text-[#F46A25] font-bold hover:underline" data-testid="help-advice-author-link">See full profile →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpAdvicePage;
